import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  establishment: "",
  message: "",
};

const DemoModal = ({ open, onClose }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.establishment) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        establishment: form.establishment,
        service: "Demande de renseignements",
        message:
          form.message ||
          `Demande de renseignements pour ${form.establishment}.`,
      });
      toast.success(
        "Votre demande de renseignements a bien été enregistrée. Nous vous recontactons sous 24h ouvrées."
      );
      setForm(initial);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Impossible d'envoyer votre demande. Réessayez."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="demo-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto"
    >
      {/* Overlay */}
      <button
        aria-label="Fermer"
        data-testid="demo-modal-overlay"
        onClick={onClose}
        className="absolute inset-0 bg-[#020510]/85 backdrop-blur-sm"
      />

      {/* Card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-modal-title"
        className="relative w-full max-w-lg bg-[#0E1530] border border-white/10 rounded-3xl shadow-2xl p-6 md:p-8 my-auto"
      >
        <button
          data-testid="demo-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Fermer la modale"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5BC4D1] font-medium">
          <ShieldCheck size={14} />
          Demande de renseignements
        </div>
        <h3
          id="demo-modal-title"
          className="font-display text-2xl md:text-3xl font-medium mt-2 leading-tight"
        >
          En savoir plus sur MeMed Key® et MeMed BV®.
        </h3>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed">
          Renseignez vos coordonnées : notre équipe vous recontacte sous 24h
          ouvrées.
        </p>

        <form
          data-testid="demo-form"
          onSubmit={submit}
          className="mt-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Nom complet *
            </label>
            <input
              data-testid="demo-name"
              type="text"
              name="name"
              value={form.name}
              onChange={onChange}
              required
              placeholder="Dr. Jean Dupont"
              className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Email professionnel *
            </label>
            <input
              data-testid="demo-email"
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              required
              placeholder="prenom.nom@hopital.fr"
              className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Téléphone *
            </label>
            <input
              data-testid="demo-phone"
              type="tel"
              name="phone"
              value={form.phone}
              onChange={onChange}
              required
              placeholder="+33 1 23 45 67 89"
              className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Établissement *
            </label>
            <input
              data-testid="demo-establishment"
              type="text"
              name="establishment"
              value={form.establishment}
              onChange={onChange}
              required
              placeholder="CHU de Bordeaux, Cabinet médical, Laboratoire..."
              className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Message (optionnel)
            </label>
            <textarea
              data-testid="demo-message"
              name="message"
              value={form.message}
              onChange={onChange}
              rows={3}
              placeholder="Précisez vos enjeux, votre service ou vos volumes estimés..."
              className="w-full bg-[#060B19] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#5BC4D1]/40 focus:border-[#5BC4D1]/40 transition-all resize-none"
            />
          </div>

          <p className="text-[11px] text-slate-500 leading-relaxed">
            En soumettant ce formulaire, vous acceptez que Nexodiag utilise vos
            informations pour vous recontacter au sujet de cette demande.
          </p>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              data-testid="demo-cancel"
              onClick={onClose}
              className="px-5 py-2.5 rounded-full text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              Annuler
            </button>
            <button
              data-testid="demo-submit"
              type="submit"
              disabled={loading}
              className="group bg-[#5BC4D1] text-[#060B19] font-semibold px-5 py-2.5 rounded-full hover:bg-[#3DA9B6] transition-colors inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  Envoyer ma demande
                  <Send size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DemoModal;
